export const EMPTY_ACTIVITY_FORM = {
  name: "",
  activity_category_id: "",
  description: "",
  thumb_image: null,
  cover_image: null,
  gallery_images: [],
  gallery: [],
  duration: "",
  location: "",
  latitude: "",
  longitude: "",
  map_link: "",
  age_limit: "",
  layout: "indoor",
  free_booking: 0,
  refundable: 0,
  time_slot_based: 0,
  status: 1,
  is_active: 1,
};

const BOOLEAN_KEYS = new Set([
  "kids_friendly",
  "pets_friendly",
  "free_booking",
  "refundable",
  "time_slot_based",
  "status",
  "is_active",
  "submit_for_approval",
  "popular",
  "recommended",
]);

export const toBooleanInt = (v) => {
  if (v === true || v === 1 || v === "1" || v === "true" || v === "on") return "1";
  if (v === false || v === 0 || v === "0" || v === "false" || v === "off") return "0";
  return "0";
};

const SKIP_ON_SAVE_KEYS = new Set(["is_approved", "submit_for_approval"]);

export const buildActivityFormData = (data, { currentStage, mediaOnly = false, generalOnly = false } = {}) => {
  const formData = new FormData();
  const skipMedia = generalOnly;
  const skipGeneral = mediaOnly;

  Object.keys(data || {}).forEach((key) => {
    if (SKIP_ON_SAVE_KEYS.has(key)) {
      return;
    }
    if (skipMedia && ["thumb_image", "cover_image", "gallery_images", "gallery"].includes(key)) {
      return;
    }
    if (skipGeneral && !["thumb_image", "cover_image", "gallery_images", "gallery"].includes(key)) {
      if (!["current_stage"].includes(key)) return;
    }

    if (key === "gallery_images") {
      if (Array.isArray(data[key])) {
        data[key].forEach((file, index) => {
          if (file instanceof File) {
            formData.append(`gallery_images[${index}]`, file);
          }
        });
      }
    } else if (key === "gallery") {
      // server gallery handled separately
    } else if (key === "thumb_image" || key === "cover_image") {
      if (data[key] instanceof File) {
        formData.append(key, data[key]);
      }
    } else if (data[key] !== null && data[key] !== undefined) {
      if (BOOLEAN_KEYS.has(key)) {
        formData.append(key, toBooleanInt(data[key]));
      } else {
        formData.append(key, data[key]);
      }
    }
  });

  if (!skipGeneral) {
    const ageLimit =
      data.age_limit === "" || data.age_limit == null ? "0" : String(data.age_limit);
    formData.set("age_limit", ageLimit);
    formData.set(
      "chargable_from",
      data.chargable_from != null && data.chargable_from !== ""
        ? String(data.chargable_from)
        : ageLimit
    );
    formData.set("address", data.address || data.location || "");
    if (!formData.has("kids_friendly")) formData.set("kids_friendly", "0");
    if (!formData.has("pets_friendly")) formData.set("pets_friendly", "0");
  }

  if (currentStage != null) {
    formData.set("current_stage", String(currentStage));
  }

  return formData;
};

export const formatRelativeSaved = (date) => {
  if (!date) return null;
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return "Saved just now";
  if (seconds < 60) return `Saved ${seconds} seconds ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `Saved ${mins} minute${mins === 1 ? "" : "s"} ago`;
  return `Saved at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

const hasImageValue = (value) =>
  value instanceof File || (typeof value === "string" && value.trim() !== "");

/** DB requires cover_image + thumb_image on insert — only create when both are present. */
export const hasRequiredMediaForCreate = (data) =>
  hasImageValue(data?.thumb_image) && hasImageValue(data?.cover_image);

export const skipAutoSave = (message) => ({ skipped: true, message });

const toFlag = (v) => (v === true || v === 1 || v === "1" ? 1 : 0);

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";

/** Laravel may serialize the relation as gallery_images or galleryImages. */
export const normalizeServerGallery = (data) => {
  const raw = data?.gallery_images ?? data?.galleryImages ?? [];
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw.filter((item) => {
    const id = item?.id;
    if (id != null) {
      if (seen.has(id)) return false;
      seen.add(id);
    }
    return true;
  });
};

const coalesceField = (local, server) => {
  if (local instanceof File) return local;
  if (hasValue(server)) return server;
  return local ?? server ?? "";
};

/** Map GET /activities/:id payload into builder form state. */
export const mapActivityApiToForm = (data, prev = null, { preferServerMedia = false } = {}) => {
  if (!data) return prev ? { ...prev } : { ...EMPTY_ACTIVITY_FORM };

  const serverGallery = normalizeServerGallery(data);
  const base = {
    name: data.name || "",
    activity_category_id: data.activity_category_id || "",
    description: data.description || "",
    thumb_image: data.thumb_image || null,
    cover_image: data.cover_image || null,
    gallery: serverGallery,
    gallery_images: [],
    duration: data.duration ?? 0,
    location: data.location || "",
    latitude: data.latitude || "",
    longitude: data.longitude || "",
    map_link: data.map_link || "",
    age_limit: data.age_limit != null ? String(data.age_limit) : "",
    layout: data.layout || "indoor",
    free_booking: toFlag(data.free_booking),
    refundable: toFlag(data.refundable),
    time_slot_based: toFlag(data.time_slot_based),
    status: toFlag(data.status ?? 1),
    is_active: toFlag(data.is_active ?? 1),
  };

  if (!prev) return base;

  const prevGallery = Array.isArray(prev.gallery) ? prev.gallery : [];
  const serverIds = new Set(serverGallery.map((item) => item?.id).filter((id) => id != null));
  const keptLocalGallery =
    prevGallery.length > 0 &&
    prevGallery.length < serverGallery.length &&
    prevGallery.every((item) => item?.id && serverIds.has(item.id));

  return {
    ...prev,
    name: coalesceField(prev.name, base.name),
    activity_category_id: coalesceField(prev.activity_category_id, base.activity_category_id),
    description: coalesceField(prev.description, base.description),
    location: coalesceField(prev.location, base.location),
    latitude: coalesceField(prev.latitude, base.latitude),
    longitude: coalesceField(prev.longitude, base.longitude),
    map_link: coalesceField(prev.map_link, base.map_link),
    age_limit: hasValue(base.age_limit) ? base.age_limit : prev.age_limit,
    layout: base.layout || prev.layout,
    duration: base.duration || prev.duration,
    free_booking: base.free_booking,
    refundable: base.refundable,
    time_slot_based: base.time_slot_based,
    status: base.status,
    is_active: base.is_active,
    thumb_image: preferServerMedia
      ? base.thumb_image || prev.thumb_image
      : prev.thumb_image instanceof File
        ? prev.thumb_image
        : base.thumb_image || prev.thumb_image,
    cover_image: preferServerMedia
      ? base.cover_image || prev.cover_image
      : prev.cover_image instanceof File
        ? prev.cover_image
        : base.cover_image || prev.cover_image,
    gallery: keptLocalGallery
      ? prevGallery
      : serverGallery.length > 0
        ? serverGallery
        : prevGallery,
    gallery_images: preferServerMedia ? [] : prev.gallery_images || [],
  };
};

